import { hasSingleStore } from '../auth/authHelpers';
import css from './navigation.module.less';
import { useStoreGuid } from '@/lib/hooks';
import { useTranslator } from '@/translations/translator';
import useInputFocusShortcut from '@/util/useInputFieldFocus';
import { Autocomplete, Paper, TextField, TextFieldProps } from '@mui/material';
import { filterTruthy } from '@whiz-cart/node-shared/helpers/filterTruthy';
import { getStoreLabel, Store, StoreStatus } from '@whiz-cart/node-shared/models/store';
import { AutoFit } from '@whiz-cart/ui-shared/autoFit/autoFit.tsx';
import { vars } from '@whiz-cart/ui-shared/theme/vars';
import { urlService } from '@whiz-cart/ui-shared/url/url.service';
import c from 'classnames';
import { orderBy, partition } from 'lodash';
import { useMemo, useRef, useState } from 'react';

const emphasizedStyle = { fontWeight: 'bold' } as const;
const deEmphasizedStyle = { opacity: 0.4 } as const;
const inputStyle = { padding: '0px', border: 'none', color: 'inherit', fontWeight: '700', lineHeight: 'normal' } as const;
const helperTextStyle = { padding: 0, margin: 0, color: vars.grey400, fontWeight: 400, lineHeight: 'normal' } as const;
const textFieldSx = { padding: '10px 0px', margin: 0, input: { color: 'white', fontSize: 'large', fontWeight: 'bold' } } as const;
const paperSx = { width: 'max-content', maxWidth: '90vw' } as const;

export type StoreSelectAutocompleteProps = {
    stores: Record<string, Store>;
    onSelect: () => void;
};

export default function StoreSelectAutocomplete({ stores, onSelect }: StoreSelectAutocompleteProps) {
    const t = useTranslator();
    const [open, setOpen] = useState(false);
    const textFieldRef = useRef<HTMLInputElement>(undefined);
    const [textFieldValue, setTextFieldValue] = useState('');

    const { storeGuid } = useStoreGuid();
    const storeName = stores[storeGuid ?? '']?.name ?? storeGuid ?? t('global');
    const externalStoreId = stores[storeGuid ?? '']?.externalStoreId;

    const options = useMemo(
        () =>
            [
                !hasSingleStore() && {
                    value: undefined,
                    label: t('global'),
                    isDeEmphasized: false,
                    isEmphasized: storeGuid === undefined || false,
                    nameAndIdText: t('global').toLowerCase(),
                    searchText: t('global').toLowerCase(),
                },
                ...orderBy(stores, 'name').map((x) => {
                    const label = getStoreLabel(x);
                    const nameAndIdText = [label, x.externalStoreId, x.storeNumber].filter(filterTruthy).join(' ').toLowerCase();
                    const addressAndGlnText = [x.globalLookupNumber, x.city, x.street, x.zipCode]
                        .filter(filterTruthy)
                        .join(' ')
                        .toLowerCase();

                    return {
                        value: x.storeGuid,
                        label,
                        isDeEmphasized: !(x.status === StoreStatus.isActive),
                        isEmphasized: x.storeGuid === storeGuid,
                        nameAndIdText,
                        searchText: `${nameAndIdText} ${addressAndGlnText}`,
                    };
                }),
            ]
                .filter(filterTruthy)
                .sort((a, b) => (a.value === storeGuid ? -1 : b.value === storeGuid ? 1 : 0)),
        [stores, storeGuid],
    );

    function selectStore(storeGuid: string | undefined) {
        urlService.pushUrl({ storeGuid });
        onSelect();
    }

    const { modifierText } = useInputFocusShortcut('k', textFieldRef.current, { requireShift: true, customAction: focusTextField });

    function focusTextField() {
        setTextFieldValue('');
        setOpen(true);
        setTimeout(() => {
            textFieldRef.current?.focus();
        });
    }

    return (
        <div className={css.selectorRoot}>
            {open && (
                <div className={css.wrapper}>
                    <Autocomplete
                        className={css.autocomplete}
                        onClose={() => {
                            setOpen(false);
                            if (textFieldRef?.current) textFieldRef.current.blur();
                        }}
                        onOpen={() => {
                            setOpen(true);
                        }}
                        blurOnSelect
                        disableClearable
                        ListboxComponent={Paper}
                        PaperComponent={CustomPaper}
                        autoHighlight
                        openOnFocus
                        options={options}
                        value={undefined}
                        renderOption={({ key, ...props }, { isDeEmphasized, label, isEmphasized }) => (
                            <li key={key} {...props} style={isEmphasized ? emphasizedStyle : undefined}>
                                <div style={isDeEmphasized ? deEmphasizedStyle : undefined}>{label}</div>
                            </li>
                        )}
                        onChange={(_event, { value }) => {
                            selectStore(value);
                        }}
                        filterOptions={(options) => {
                            const terms = textFieldValue.toLowerCase().split(/\s+/).filter(Boolean);
                            const matches = (text: string) => terms.every((term) => text.includes(term));
                            const [nameOrIdHits, otherHits] = partition(
                                options.filter((option) => matches(option.searchText)),
                                (option) => matches(option.nameAndIdText),
                            );
                            return [...nameOrIdHits, ...otherHits];
                        }}
                        renderInput={(params) => {
                            return (
                                <StyledTextField
                                    {...params}
                                    inputRef={textFieldRef}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: <div className={css.modifierText}>{modifierText}</div>,
                                    }}
                                    onChange={(e) => setTextFieldValue(e.target.value)}
                                />
                            );
                        }}
                    />
                </div>
            )}
            <div
                className={c(css.storeSelector, { [css.hidden]: open })}
                tabIndex={0}
                onClick={() => focusTextField()}
                onFocus={() => focusTextField()}
            >
                <AutoFit disableRotate optimizeWrap align="left" className={css.storeSelectorFit}>
                    {storeName}
                    {externalStoreId && <span className={css.storeId}> {externalStoreId}</span>}
                </AutoFit>
            </div>
        </div>
    );
}

function StyledTextField(props: TextFieldProps & { focusTextField?: () => void }) {
    const { InputProps, focusTextField, ...rest } = props;

    return (
        <TextField
            {...rest}
            InputProps={{
                ...InputProps,
                style: inputStyle,
            }}
            FormHelperTextProps={{
                style: helperTextStyle,
                onClick: focusTextField,
            }}
            sx={textFieldSx}
            margin="none"
        />
    );
}

function CustomPaper(props: any) {
    return <Paper {...props} sx={paperSx} />;
}
